import { z } from 'zod'
import namesJson from '../names.json'
import { ConfigError, formatIssues } from './errors'

/**
 * Display-name rule shared by the server, the client and `set_display_name` in
 * `infra/supabase/migrations/0001_init.sql`.
 */
export const DISPLAY_NAME_RE = /^[A-Za-z0-9_-]{3,24}$/

/**
 * What a player may set as their name (D-039, no free text): `adjective-animal-N`, N 1–99, at
 * most 24 chars. Same rule as `set_display_name` in `infra/supabase/migrations/0004_names.sql`;
 * every `generateName` result passes it. Older rows may still hold names of the 0001 shape.
 */
export const PICKED_NAME_RE = /^[a-z]+-[a-z]+-[0-9]{1,2}$/

// Words are ≤ 9 letters so `adjective-animal-NN` is at most 23 chars, inside DISPLAY_NAME_RE.
const word = z.string().regex(/^[a-z]{1,9}$/)
const namesSchema = z.strictObject({
  adjectives: z.array(word).min(1),
  animals: z.array(word).min(1),
})

export interface NameWords {
  readonly adjectives: readonly string[]
  readonly animals: readonly string[]
}

function loadNames(raw: unknown): NameWords {
  const parsed = namesSchema.safeParse(raw)
  if (!parsed.success) {
    throw new ConfigError(`Invalid config\n${formatIssues('names', parsed.error)}`)
  }
  return Object.freeze({
    adjectives: Object.freeze(parsed.data.adjectives),
    animals: Object.freeze(parsed.data.animals),
  })
}

/** One word list for guests and bots (D-024), so bot names look like player names. */
export const NAME_WORDS: NameWords = loadNames(namesJson)

function pick(list: readonly string[], random: () => number): string {
  return list[Math.floor(random() * list.length)] ?? list[0]!
}

/** `adjective-animal-NN`, e.g. `sunny-otter-42`. Never used inside the sim. */
export function generateName(random: () => number): string {
  const nn = 10 + Math.floor(random() * 90)
  return `${pick(NAME_WORDS.adjectives, random)}-${pick(NAME_WORDS.animals, random)}-${nn}`
}
